import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

import Navbar from './components/Navbar';
import Home from './pages/Home';
import ProtectedRoute from './components/auth/ProtectedRoute';
import './index.css';
import './membership.css';
import { CartFlightLayer } from './context/CartContext';
import Footer from './components/Footer';

const Shop = lazy(() => import('./pages/Shop'));
const BestSellers = lazy(() => import('./pages/BestSellers'));
const Products = lazy(() => import('./pages/Products'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const NewArrivals = lazy(() => import('./pages/NewArrivals'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const PlayerCollection = lazy(() => import('./components/player/PlayerCollection'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminManagement').then(({ AdminUsers: Component }) => ({ default: Component })));
const AdminMemberships = lazy(() => import('./pages/admin/AdminManagement').then(({ AdminMemberships: Component }) => ({ default: Component })));
const AdminOrders = lazy(() => import('./pages/admin/AdminManagement').then(({ AdminOrders: Component }) => ({ default: Component })));
const AdminProducts = lazy(() => import('./pages/admin/AdminManagement').then(({ AdminProducts: Component }) => ({ default: Component })));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Profile = lazy(() => import('./pages/Profile'));
const Orders = lazy(() => import('./pages/Orders'));
const Membership = lazy(() => import('./pages/Membership'));
const MembershipCardPage = lazy(() => import('./pages/Membership').then(({ MembershipCardPage: Component }) => ({ default: Component })));
const MembershipVerification = lazy(() => import('./pages/Membership').then(({ MembershipVerification: Component }) => ({ default: Component })));
const FooterInfo = lazy(() => import('./pages/FooterInfo'));

const HashScroll = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [pathname, hash]);

  return null;
};

const FooterVisibility = ({ children }) => {
  const { pathname } = useLocation();
  const hideFooter = pathname.startsWith('/admin')
    || pathname === '/checkout'
    || pathname === '/profile'
    || pathname === '/orders'
    || pathname === '/membership'
    || pathname.startsWith('/membership/');
  return hideFooter ? null : children;
};

function App() {
  return (
    <Router>
      <div className="cab-app-shell text-white">
        <Navbar />
        <HashScroll />
        <CartFlightLayer />
        <main className="cab-main">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/best-sellers" element={<BestSellers />} />
              <Route path="/new-arrivals" element={<NewArrivals />} />
              <Route path="/products" element={<Products />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/players/:playerId" element={<PlayerCollection />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/membership/card" element={<ProtectedRoute><MembershipCardPage /></ProtectedRoute>} />
              <Route path="/membership/verify/:memberId" element={<MembershipVerification />} />
              <Route path="/club" element={<FooterInfo />} />
              <Route path="/help/:topic" element={<FooterInfo />} />
              <Route path="/admin/secure/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="memberships" element={<AdminMemberships />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="products" element={<AdminProducts />} />
              </Route>
              <Route path="/admin/*" element={<Navigate to="/" replace />} />
              <Route
                path="*"
                element={
                  <div className="cab-empty-state">
                    <h1>Page Not Found</h1>
                    <Link to="/" className="gold-button gold-button--dark">
                      Go Home
                    </Link>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </main>

        <FooterVisibility><Footer /></FooterVisibility>
      </div>
    </Router>
  );
}

export default App;
